import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { AssetsService } from "../../assets.service";
import { ActivatedRoute, Router } from "@angular/router";
import { UtilsService } from "../../../../auth/service/utils.service";
import { AssetCategoriesService } from "../../asset-categories.service";
import { AuthenticationService } from "../../../../auth/service";
import moment from "moment";
import { IContentHeader } from "app/layout/components/content-header/content-header.component";
import { IAsset, IAssetCategory, IAssetForm } from "../../models/assets";

@Component({
  selector: "app-assets-add",
  templateUrl: "./assets-form.component.html",
  styleUrls: ["./assets-form.component.scss"],
})
export class AssetsFormComponent implements OnInit {
  public contentHeader: IContentHeader;
  public formGroup: FormGroup;
  public isEdit: boolean = false;
  public asset_id: number = 0;
  public showErrors: boolean = false;
  public categoriesList: IAssetCategory[] = [];
  public assetSelectedEdit: IAsset = null;
  constructor(
    public _assetService: AssetsService,
    public _formBuilder: FormBuilder,
    private activated_route: ActivatedRoute,
    private router: Router,
    public _utilService: UtilsService,
    public _assetCategoriesService: AssetCategoriesService,
    public _authenticationService: AuthenticationService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    this._utilService.disableAutoHideLoading();
    this.activated_route.params.subscribe(async (params) => {
      if (params.hasOwnProperty("id")) {
        this.isEdit = true;
        this.asset_id = Number(params.id);
      }
    });
  }

  async ngOnInit() {
    if (this.isEdit) {
      if (!this._authenticationService.hasCapability("edit_asset")) {
        await this.router.navigateByUrl("/dashboard/main");
        return false;
      }
      this.assetSelectedEdit = await this._assetService.get(this.asset_id);
    } else {
      if (!this._authenticationService.hasCapability("create_asset")) {
        await this.router.navigateByUrl("/dashboard/main");
        return false;
      }
    }
    this.categoriesList = await this._assetCategoriesService.getCategoriesList();
    this.formGroup = this._formBuilder.group({
      name: new FormControl("", [Validators.required, Validators.minLength(3), Validators.maxLength(255)]),
      category: new FormControl("", [Validators.required]),
      description: new FormControl("", [Validators.required, Validators.minLength(3), Validators.maxLength(65535)]),
      serial: new FormControl("", [Validators.required, Validators.minLength(3), Validators.maxLength(100)]),
      contract: new FormControl("", Validators.maxLength(100)),
      rented: new FormControl(false),
      license_auto_renew: new FormControl(false),
      contract_end_date: new FormControl(null),
      license_end_date: new FormControl(null),
      product_serial_number: new FormControl("", [Validators.required]),
    });
    if (this.isEdit) {
      if (!this.assetSelectedEdit) {
        await this.router.navigateByUrl("/dashboard/assets/list");
        return false;
      } else {
        this.formGroup.patchValue({
          name: this.assetSelectedEdit.name ? this.assetSelectedEdit.name : "",
          category: this.assetSelectedEdit.category ? this.assetSelectedEdit.category : "",
          description: this.assetSelectedEdit.description ? this.assetSelectedEdit.description : "",
          serial: this.assetSelectedEdit.serial ? this.assetSelectedEdit.serial : "",
          product_serial_number: this.assetSelectedEdit.product_serial_number ? this.assetSelectedEdit.product_serial_number : "",
          contract: this.assetSelectedEdit.contract ? this.assetSelectedEdit.contract : "",
          rented: !!this.assetSelectedEdit.rented,
          contract_end_date: this.assetSelectedEdit.contract_end_date ? moment(this.assetSelectedEdit.contract_end_date).format("YYYY-MM-DD") : null,
          license_end_date: this.assetSelectedEdit.license_end_date ? moment(this.assetSelectedEdit.license_end_date).format("YYYY-MM-DD") : null,
          license_auto_renew: this.assetSelectedEdit.license_auto_renew,
        });
      }
    }
    this.contentHeader = {
      headerTitle: this.isEdit ? "EDIT_ASSET" : "ADD_ASSET",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: "MENU.ASSETS",
            isLink: true,
            link: "/dashboard/assets/list",
          },
          {
            name: this.isEdit ? "EDIT" : "ADD",
            isLink: false,
          },
        ],
      },
    };
    this._utilService.hideLoading();
    this._changeDetectorRef.detectChanges();
  }

  fieldHasErrors(field_name: string): boolean {
    if (this.showErrors) {
      return this.formGroup.controls[field_name].status === "INVALID";
    } else {
      return this.formGroup.controls[field_name].status === "INVALID" && this.formGroup.controls[field_name].touched;
    }
  }

  takeDate(event: any, field: string) {
    if (field === "license_end_date") {
      this.formGroup.patchValue({
        license_end_date: event,
      });
    }
    if (field === "contract_end_date") {
      this.formGroup.patchValue({
        contract_end_date: event,
      });
    }
  }

  async create() {
    this.showErrors = true;
    // controllo valori
    if (this.formGroup.invalid) {
      return false;
    }
    if (!!this.formGroup.value.contract && !this.formGroup.value.contract_end_date) {
      await this._utilService.showSWALError({ text: "INSERT_DATE_END_CONTRACT" });
      return false;
    }
    if (!!this.formGroup.value.contract_end_date && !this.formGroup.value.contract) {
      await this._utilService.showSWALError({ text: "INSERT_CONTRACT" });
      return false;
    }
    if (this.formGroup.value.license_auto_renew && !this.formGroup.value.license_end_date) {
      await this._utilService.showSWALError({ text: "INSERT_DATE_END_LICENSE" });
      return false;
    }
    let data: IAssetForm = {
      name: this.formGroup.value.name.trim(),
      category: this.formGroup.value.category,
      description: this.formGroup.value.description,
      serial: this.formGroup.value.serial.trim(),
      product_serial_number: this.formGroup.value.product_serial_number.trim(),
      contract: this.formGroup.value.contract.trim(),
      rented: Number(this.formGroup.value.rented),
      contract_end_date: this.formGroup.value.contract_end_date,
      license_end_date: this.formGroup.value.license_end_date,
      license_auto_renew: this.formGroup.value.license_auto_renew,
    };

    this._utilService.showLoading();
    await this._assetService
      .createAsset(data)
      .then(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_UPLOAD_ASSET" });
        await this.router.navigateByUrl("/dashboard/assets/list");
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_UPLOAD_ASSET" });
      });
  }

  async update() {
    this.showErrors = true;
    // controllo valori
    if (this.formGroup.invalid) {
      return false;
    }
    if (!!this.formGroup.value.contract && !this.formGroup.value.contract_end_date) {
      await this._utilService.showSWALError({ text: "INSERT_DATE_END_CONTRACT" });
      return false;
    }
    if (!!this.formGroup.value.contract_end_date && !this.formGroup.value.contract) {
      await this._utilService.showSWALError({ text: "INSERT_CONTRACT" });
      return false;
    }
    if (this.formGroup.value.license_auto_renew && !this.formGroup.value.license_end_date) {
      await this._utilService.showSWALError({ text: "INSERT_DATE_END_LICENSE" });
      return false;
    }
    let data: IAssetForm = {
      name: this.formGroup.value.name.trim(),
      category: this.formGroup.value.category,
      description: this.formGroup.value.description,
      serial: this.formGroup.value.serial.trim(),
      product_serial_number: this.formGroup.value.product_serial_number.trim(),
      contract: this.formGroup.value.contract.trim(),
      rented: Number(this.formGroup.value.rented),
      contract_end_date: this.formGroup.value.contract_end_date,
      license_end_date: this.formGroup.value.license_end_date,
      license_auto_renew: this.formGroup.value.license_auto_renew,
    };

    this._utilService.showLoading();
    await this._assetService
      .updateAsset(this.asset_id, data)
      .then(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_EDIT_ASSET" });
        await this.router.navigateByUrl("/dashboard/assets/list");
      })
      .catch(() => {
        this._utilService.hideLoading();
        this._utilService.showSWALError({ text: "ERROR_EDIT_ASSET" });
      });
  }

  resetLicense() {
    this.formGroup.patchValue({
      license_end_date: null,
    });
    const licenseDate: any = document.querySelector("#license_end_date input");
    licenseDate.value = "";
  }

  resetContractEndDate() {
    this.formGroup.patchValue({
      contract_end_date: null,
    });
    const contractDate: any = document.querySelector("#contract_end_date input");
    contractDate.value = "";
  }
}
